import type { ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '@shared/lib/cn';
import { BADGE, BADGE_BRASS, LABEL, ROW, ROW_LINE, ROW_TITLE } from '../visitorAccessStyles';

export interface RowProps {
  title: ReactNode;
  line?: ReactNode;
  /** An icon, or the tick while picking people. */
  leading?: ReactNode;
  /** A badge or a small button on the right. */
  trailing?: ReactNode;
  chevron?: boolean;
  onClick?: () => void;
  pressed?: boolean;
  className?: string;
}

/**
 * One row on the Visitor access tabs: a name over a muted line. Rows sit alone on a card or
 * stacked inside one; a row that opens something is a button.
 */
export function Row({ title, line, leading, trailing, chevron, onClick, pressed, className }: RowProps): JSX.Element {
  const content = (
    <>
      {leading}
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className={cn(ROW_TITLE, 'line-clamp-2')}>{title}</span>
        {line ? <span className={cn(ROW_LINE, 'line-clamp-2')}>{line}</span> : null}
      </span>
      {trailing}
      {chevron ? <ChevronRight className="h-4 w-4 shrink-0 text-ink-muted" strokeWidth={2} /> : null}
    </>
  );

  return onClick ? (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={pressed}
      className={cn(ROW, 'transition-transform active:scale-[0.99]', className)}
    >
      {content}
    </button>
  ) : (
    <div className={cn(ROW, className)}>{content}</div>
  );
}

/** The state badge on a row: green by default, brass for "Regular", "On site" and bookings. */
export function Badge({ brass, children }: { brass?: boolean; children: ReactNode }): JSX.Element {
  return <span className={brass ? BADGE_BRASS : BADGE}>{children}</span>;
}

/** A labelled block on the tabs: the eyebrow (with an optional link on the right) 10px above its content. */
export function Block({ label, action, children }: { label: string; action?: ReactNode; children: ReactNode }): JSX.Element {
  return (
    <section className="flex flex-col gap-2.5">
      {action ? (
        <div className="flex items-center justify-between">
          <p className={LABEL}>{label}</p>
          {action}
        </div>
      ) : (
        <p className={LABEL}>{label}</p>
      )}
      {children}
    </section>
  );
}
